import { Upload } from "lucide-react";
import { useEffect, useState } from "react";
import { Alert, AlertDescription } from "../../../../components/ui/alert";
import { OperationConfirmDialog } from "../../../../components/ui/operation-confirm-dialog";
import { Button } from "../../../../components/ui/button";
import { Card, CardContent } from "../../../../components/ui/card";
import { Separator } from "../../../../components/ui/separator";
import { getFundDbName } from "../../../../utils/funds";
import { IndriveCheckoutItemCard } from "./components/indrive-checkout-item-card";
import { IndriveCheckoutItemSkeleton } from "./components/indrive-checkout-item-skeleton";
import { useIndriveCheckout } from "./hooks/use-indrive-checkout";

export default function IndriveCheckoutResolution() {
  const selectedFund = getFundDbName("indrive");
  const [openDialog, setOpenDialog] = useState(false);
  const [alert, setAlert] = useState<{
    show: boolean;
    message: string;
    type: "success" | "error" | "warning";
  }>({
    show: false,
    message: "",
    type: "success",
  });

  const {
    checkoutList,
    isLoading,
    confirmCheckout,
    startDoc,
    submitRemessa,
    pushIndrive,
    isConfirming,
    isStartingDoc,
    isSubmittingRemessa,
    isPushingIndrive,
    pushSuccess,
    pushError,
  } = useIndriveCheckout(selectedFund);

  useEffect(() => {
    if (pushSuccess) {
      setAlert({
        show: true,
        message: "Push Indrive realizado com sucesso!",
        type: "success",
      });
      setOpenDialog(false);
    }
  }, [pushSuccess]);

  useEffect(() => {
    if (pushError) {
      setAlert({
        show: true,
        message: "Erro ao fazer push Indrive. Tente novamente.",
        type: "error",
      });
    }
  }, [pushError]);

  useEffect(() => {
    if (alert.show) {
      const timeout = setTimeout(() => {
        setAlert({ ...alert, show: false });
      }, 6000);
      return () => clearTimeout(timeout);
    }
  }, [alert]);

  const handlePushIndrive = () => {
    if (!selectedFund) {
      setAlert({
        show: true,
        message: "Selecione um fundo antes de fazer o push.",
        type: "warning",
      });
      setOpenDialog(false);
      return;
    }
    pushIndrive();
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-foreground">
            Checkout Indrive
          </h1>
          <p className="text-muted-foreground mt-1">
            Resolver pendências dos checkouts do Indrive
          </p>
        </div>
        <div className="space-y-6">
          <IndriveCheckoutItemSkeleton />
          <Separator className="my-6" />
          <IndriveCheckoutItemSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              Checkout Indrive
            </h1>
            <p className="text-muted-foreground mt-1">
              Resolver pendências dos checkouts do Indrive
            </p>
          </div>

          <div className="flex items-center gap-4">
            <Button
              variant="default"
              onClick={() => setOpenDialog(true)}
              disabled={isPushingIndrive || !selectedFund}
              className="gap-2"
            >
              <Upload className="h-4 w-4" />
              {isPushingIndrive ? "Processando..." : "Push Indrive"}
            </Button>
          </div>
        </div>
      </div>

      {alert.show && (
        <Alert
          variant={alert.type === "error" ? "destructive" : "default"}
          className="mb-6"
        >
          <AlertDescription>{alert.message}</AlertDescription>
        </Alert>
      )}

      {selectedFund && checkoutList.length === 0 && !isLoading && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-foreground">
                Não há Checkouts de INDRIVE para serem finalizados dos últimos
                20 dias
              </h2>
            </div>
          </CardContent>
        </Card>
      )}

      {selectedFund && !isLoading && checkoutList.length > 0 && (
        <div className="space-y-6">
          {(() => {
            const latestDate = checkoutList.reduce((max, item) => {
              const d = item.date_to_checkout.slice(0, 10);
              return d > max ? d : max;
            }, "");
            return checkoutList.map((checkout, index) => (
              <div key={`${checkout.date_to_checkout}-${index}`}>
                <IndriveCheckoutItemCard
                  checkout={checkout}
                  hasNewerDate={checkout.date_to_checkout.slice(0, 10) < latestDate}
                  onConfirmCheckout={(filterDate, ops) =>
                    confirmCheckout({ filterDate, ops })
                  }
                  onStartDoc={(filterDate, ops) => startDoc({ filterDate, ops })}
                  onSubmitRemessa={(filterDate, ops) =>
                    submitRemessa({ filterDate, ops })
                  }
                  isConfirming={isConfirming}
                  isStartingDoc={isStartingDoc}
                  isSubmittingRemessa={isSubmittingRemessa}
                />
                {index < checkoutList.length - 1 && (
                  <Separator className="my-6" />
                )}
              </div>
            ));
          })()}
        </div>
      )}

      <OperationConfirmDialog
        open={openDialog}
        onOpenChange={setOpenDialog}
        onConfirm={handlePushIndrive}
        action="push"
        ops="indrive"
        details={[{ label: "Banco", value: selectedFund || "Nenhum selecionado" }]}
        loading={isPushingIndrive}
      />
    </div>
  );
}
